/**
 * Item catalogue. Item INSTANCES only ever exist server-side (DB table `items`); the client just
 * renders what the server reports. Contraband here is deliberately fictional and stylised — game
 * tokens with a name and a price, not real substances, and no production instructions of any kind.
 *
 * Economy tuning (prices, demand, production) belongs to economy-engineer (server/economy); this file
 * only defines what the items ARE so every layer agrees on ids, sizes and legality.
 */

export type ItemCategory = 'contraband' | 'goods' | 'valuables' | 'equipment' | 'weapon' | 'ammo';
export type OwnerType = 'character' | 'vehicle' | 'property' | 'gang' | 'world';

export interface ItemDef {
  id: string;
  /** Singular display name, e.g. "crate of stolen fur coats". */
  name: string;
  plural: string;
  category: ItemCategory;
  /** Legal goods are never confiscated and never raise heat when searched. */
  legal: boolean;
  description: string;
  /** Storage units one item occupies (vehicle boots / lockups have unit capacities). */
  size: number;
  /** Most a character can carry on foot. Bigger loads need a vehicle — and can be intercepted. */
  maxOnFoot: number;
  /** Reference value per unit in £ before market modifiers (economy-engineer owns real pricing). */
  baseValue: number;
  /** How suspicious one unit is if police search you (feeds the heat model; 0 for legal goods). */
  suspicion: number;
  /**
   * Weapons online (shared/weapons.ts): the weapon id (src/weapons/weaponData.ts) this item IS
   * (category 'weapon') or the gun these rounds feed (category 'ammo').
   */
  weapon?: string;
  /** Small things (rounds): this many share one storage unit of `size` (default 1). */
  perUnit?: number;
}

/** Quality tiers multiply value; production upgrades raise tier. */
export const QUALITY_TIERS = [
  { tier: 0, name: 'Standard', valueMul: 1 },
  { tier: 1, name: 'Fine', valueMul: 1.35 },
  { tier: 2, name: 'Prime', valueMul: 1.8 },
] as const;

const D = (d: ItemDef): ItemDef => d;

export const ITEM_DEFS = {
  // ---- contraband (fictional)
  velvet: D({
    id: 'velvet',
    name: 'crate of stolen fur coats',
    plural: 'crates of stolen fur coats',
    category: 'contraband',
    legal: false,
    description: 'Mink and fox furs from a West End warehouse job: the most valuable goods on the docks. Bulky: one crate on foot, more needs a car boot.',
    size: 4,
    maxOnFoot: 1,
    baseValue: 260,
    suspicion: 6,
  }),
  'blue-tins': D({
    id: 'blue-tins',
    name: 'stolen wristwatch',
    plural: 'stolen wristwatches',
    category: 'contraband',
    legal: false,
    description: 'Stolen wristwatches: cheap to buy and small, so a dozen fit in your pockets. Easy money a little at a time.',
    size: 1,
    maxOnFoot: 12,
    baseValue: 45,
    suspicion: 1,
  }),
  'ember-gin': D({
    id: 'ember-gin',
    name: 'case of smuggled perfume',
    plural: 'cases of smuggled perfume',
    category: 'contraband',
    legal: false,
    description: 'French perfume brought in without paying customs duty, so it sells well under the shop price. Heavy glass: it clinks round corners.',
    size: 3,
    maxOnFoot: 1,
    baseValue: 140,
    suspicion: 3,
  }),
  'moth-silk': D({
    id: 'moth-silk',
    name: 'bolt of smuggled silk',
    plural: 'bolts of smuggled silk',
    category: 'contraband',
    legal: false,
    description: 'Silk cloth brought in past customs. Tailors and dress shops pay well for it and ask no questions.',
    size: 2,
    maxOnFoot: 2,
    baseValue: 95,
    suspicion: 2,
  }),
  // ---- produced goods (production properties, shared/properties.ts). Fictional knock-offs: an
  // operation turns bought-in supplies plus server time into these; there is no recipe anywhere.
  'tape-decks': D({
    id: 'tape-decks',
    name: 'boxed knock-off tape deck',
    plural: 'boxed knock-off tape decks',
    category: 'contraband',
    legal: false,
    description: 'Fake-brand cassette decks made in your own premises; fences and the big buyers take them. They play, for a while.',
    size: 2,
    maxOnFoot: 2,
    baseValue: 90,
    suspicion: 1,
  }),
  'tax-discs': D({
    id: 'tax-discs',
    name: 'bundle of forged tax discs',
    plural: 'bundles of forged tax discs',
    category: 'contraband',
    legal: false,
    description: 'Forged road-tax discs for car windscreens, made in your own premises. Good enough through a windscreen.',
    size: 1,
    maxOnFoot: 6,
    baseValue: 70,
    suspicion: 2,
  }),
  'cut-spirits': D({
    id: 'cut-spirits',
    name: 'case of cut-price spirits',
    plural: 'cases of cut-price spirits',
    category: 'contraband',
    legal: false,
    description: 'Cheap spirits with no duty paid, bottled in your own premises. Pubs pour it into the good bottles after closing.',
    size: 3,
    maxOnFoot: 1,
    baseValue: 150,
    suspicion: 2,
  }),
  'hooky-cigs': D({
    id: 'hooky-cigs',
    name: 'carton of stolen cigarettes',
    plural: 'cartons of stolen cigarettes',
    category: 'contraband',
    legal: false,
    description: 'Cigarettes off a stolen lorry, re-wrapped with a fake duty stamp. Small, light and easy to sell.',
    size: 1,
    maxOnFoot: 6,
    baseValue: 50,
    suspicion: 1,
  }),
  // ---- legal goods (courier / delivery work)
  parcel: D({
    id: 'parcel',
    name: 'parcel',
    plural: 'parcels',
    category: 'goods',
    legal: true,
    description: 'Brown paper and string. Honest work.',
    size: 1,
    maxOnFoot: 4,
    baseValue: 0,
    suspicion: 0,
  }),
  'scrap-metal': D({
    id: 'scrap-metal',
    name: 'load of scrap',
    plural: 'loads of scrap',
    category: 'goods',
    legal: true,
    description: 'Copper pipe, radiators and a bicycle. Gorringe weighs it and pays by the pound.',
    size: 3,
    maxOnFoot: 1,
    baseValue: 30,
    suspicion: 0,
  }),
  // ---- valuables (burglary / robbery loot, fenced)
  'hot-goods': D({
    id: 'hot-goods',
    name: 'bag of stolen valuables',
    plural: 'bags of stolen valuables',
    category: 'valuables',
    legal: false,
    description: 'Burglary loot: clocks, silver, somebody\'s gran\'s brooch. Sell it to a fence (a buyer of stolen goods).',
    size: 1,
    maxOnFoot: 3,
    baseValue: 70,
    suspicion: 2,
  }),
  // ---- equipment & status (money sinks; equipment never counts against carry capacity)
  holdall: D({
    id: 'holdall',
    name: 'canvas holdall',
    plural: 'canvas holdalls',
    category: 'equipment',
    legal: true,
    description: 'Army-surplus, deep and anonymous. Lets you carry a bit more on foot.',
    size: 1,
    maxOnFoot: 1,
    baseValue: 0,
    suspicion: 0,
  }),
  'police-scanner': D({
    id: 'police-scanner',
    name: 'police scanner',
    plural: 'police scanners',
    category: 'equipment',
    legal: true,
    description: 'Listens in on the police radio: Word on the street shows exact police numbers per district instead of quiet / watchful / heavy.',
    size: 1,
    maxOnFoot: 1,
    baseValue: 0,
    suspicion: 0,
  }),
  'tailored-suit': D({
    id: 'tailored-suit',
    name: 'tailored suit',
    plural: 'tailored suits',
    category: 'equipment',
    legal: true,
    description: 'Savile Row by way of Bell Street. Does nothing except tell everyone you have arrived.',
    size: 1,
    maxOnFoot: 1,
    baseValue: 0,
    suspicion: 0,
  }),
  // ---- weapons and rounds (online arsenal, shared/weapons.ts). Guns and rounds are illegal: the police
  // confiscate them on a bust and stop-and-search notices them. Melee weapons are legal to carry (as
  // offline: a nicking takes the guns, not the bat). Everything here drops when you die.
  'wpn-cosh': D({ id: 'wpn-cosh', name: 'leather cosh', plural: 'leather coshes', category: 'weapon', legal: true, weapon: 'cosh', description: 'Lead shot in a leather sock. Puts a man down quiet.', size: 1, maxOnFoot: 1, baseValue: 30, suspicion: 0 }),
  'wpn-bat': D({ id: 'wpn-bat', name: 'cricket bat', plural: 'cricket bats', category: 'weapon', legal: true, weapon: 'bat', description: 'Long reach, slow swing. Willow, linseed oil and a name inked on the handle.', size: 2, maxOnFoot: 1, baseValue: 40, suspicion: 0 }),
  'wpn-crowbar': D({ id: 'wpn-crowbar', name: 'crowbar', plural: 'crowbars', category: 'weapon', legal: true, weapon: 'crowbar', description: 'Pops a door, swings heavy. Every builder has one; so does every burglar.', size: 1, maxOnFoot: 1, baseValue: 25, suspicion: 0 }),
  'wpn-knife': D({ id: 'wpn-knife', name: 'flick knife', plural: 'flick knives', category: 'weapon', legal: true, weapon: 'knife', description: 'Quick and close. Very nasty.', size: 1, maxOnFoot: 1, baseValue: 30, suspicion: 0 }),
  'wpn-revolver': D({ id: 'wpn-revolver', name: '.38 revolver', plural: '.38 revolvers', category: 'weapon', legal: false, weapon: 'revolver', description: 'Six-shot, ex-service, numbers filed off. Loud enough to bring the whole station.', size: 1, maxOnFoot: 1, baseValue: 450, suspicion: 4 }),
  'wpn-sawnoff': D({ id: 'wpn-sawnoff', name: 'sawn-off shotgun', plural: 'sawn-off shotguns', category: 'weapon', legal: false, weapon: 'sawnoff', description: 'Both barrels, short and wide. Nothing clears a pub faster.', size: 2, maxOnFoot: 1, baseValue: 400, suspicion: 5 }),
  'ammo-38': D({ id: 'ammo-38', name: '.38 round', plural: '.38 rounds', category: 'ammo', legal: false, weapon: 'revolver', description: 'Brass and lead in a greasy paper box. A box of 30 fits in a coat pocket.', size: 1, perUnit: 30, maxOnFoot: 30, baseValue: 4, suspicion: 0.1 }),
  'ammo-12': D({ id: 'ammo-12', name: '12-bore cartridge', plural: '12-bore cartridges', category: 'ammo', legal: false, weapon: 'sawnoff', description: 'Red paper cases, brass heads. Sixteen to a carton.', size: 1, perUnit: 16, maxOnFoot: 16, baseValue: 5, suspicion: 0.1 }),
} satisfies Record<string, ItemDef>;

export type ItemDefId = keyof typeof ITEM_DEFS;

export function isItemDefId(id: string): id is ItemDefId {
  return Object.prototype.hasOwnProperty.call(ITEM_DEFS, id);
}

export function getItemDef(id: string): ItemDef | undefined {
  return isItemDefId(id) ? ITEM_DEFS[id] : undefined;
}

export const OWNER_TYPES: readonly OwnerType[] = ['character', 'vehicle', 'property', 'gang', 'world'];
